import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  DataGrid,
  LoadPanel,
  SelectBox,
  TextBox,
} from "devextreme-react";
import {
  Column,
  Export,
  Pager,
  Paging,
  Scrolling,
  SearchPanel,
  Selection,
  Toolbar,
} from "devextreme-react/data-grid";
import { Item } from "devextreme-react/toolbar";
import CustomPageHeader from "../../components/customPageHeader/CustomPageHeader";
import BreadCrumb from "../../components/breadCrumb/BreadCrumb";
import { SubText } from "../../components/typographyText/TypographyText";
import { ContextMenu } from "devextreme-react/context-menu";
import { ToastDisplayer } from "../../components/toastDisplay/ToastDisplayer";
import {
  deleteCustomerContact,
  fetchCustomerContacts,
} from "../../api/companyCustomerApi";
import { useNavigate } from "react-router-dom";
import { custStatusDataSource } from "../customers/data";
import "./Contact.scss";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { exportDataGrid } from "devextreme/excel_exporter";

export default function Contact() {
  const navigate = useNavigate();
  const dataGridRef = useRef(null);
  const [contactDataSource, setContactDataSource] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [visibleCount, setVisibleCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const getStatusColor = (status) => {
    const statusColors = {
      Active: "#0C6230",
      Inactive: "#910000",
    };
    return statusColors[status];
  };

  //API to load contacts
  const loadContacts = async () => {
    try {
      setLoading(true);

      const result = await fetchCustomerContacts();

      if (result.success) {
        setContactDataSource(result.data);
        setVisibleCount(result.data.length);
      } else {
        ToastDisplayer("error", result.message);
      }
    } catch (error) {
      ToastDisplayer("error", "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  //To Call Contact List API on initial Render
  useEffect(() => {
    loadContacts();
  }, []);

  //Function To Status Based Filtering
  const handleStatusFilterChange = (newFilter) => {
    setStatusFilter(newFilter);

    const grid = dataGridRef.current?.instance;
    if (!grid) return;

    if (newFilter === "All") {
      grid.clearFilter();
    } else {
      grid.filter(["status", "=", newFilter]);
    }
  };

  //Function to mask the phone number :
  const maskPhoneNumber = (phone) => {
    if (!phone) return "";

    const visiblePart = phone.slice(0, 4);
    const maskedPart = "X".repeat(phone.length - 4);

    return visiblePart + maskedPart;
  };

  const actionTemplate = (cellData) => {
    const contacttransid = cellData?.data?.contacttransid;
    // console.log("transid", transid);
    if (!contacttransid) return null;

    const sanitizedClassName = `actionbtn-${contacttransid}`;

    const actionMenuItems = [
      {
        icon: "edit",
        text: "Edit & View",
        action: "edit",
      },
      {
        icon: "trash",
        text: "Delete",
        action: "delete",
      },
    ];

    return (
      <>
        <div className="nextPageBtn">
          <div className={sanitizedClassName}>
            <i className="ri-more-fill"></i>
          </div>
        </div>

        <ContextMenu
          items={actionMenuItems}
          target={`.${sanitizedClassName}`}
          showEvent="dxclick"
          cssClass="actionMenu"
          onItemClick={(e) => {
            if (e.itemData.action === "edit") {
              navigate("/contact/Edit-Contact", {
                state: { contacttransid },
              });
            }

            if (e.itemData.action === "delete") {
              handleDelete(contacttransid);
            }
          }}
        />
      </>
    );
  };

  const handleDelete = async (transid) => {
    if (!transid) return;

    try {
      setLoading(true);

      const response = await deleteCustomerContact(transid);

      if (response?.status === 200 || response?.status === 204) {
        ToastDisplayer("success", "Contact deleted successfully");
        await loadContacts(); // refresh grid
      } else {
        ToastDisplayer("error", response.message || "Failed to delete contact");
      }
    } catch (error) {
      ToastDisplayer(
        "error",
        error?.response?.data?.message || "Delete failed",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    navigate("/contact/Add-Contact");
  };

  // export functionality for custom  Export Button
  const handleExport = () => {
    const grid = dataGridRef.current?.instance;
    if (!grid) return;

    const selectedKeys = grid.getSelectedRowKeys();

    if (selectedKeys.length > 0) {
      grid.exportToExcel(true); // selected rows
    } else {
      grid.exportToExcel(false); // fallback → all rows
    }
  };
  const onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Tickets");

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      const now = new Date();

      const formattedDate = `${now.getFullYear()}-${String(
        now.getMonth() + 1,
      ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

      const fileName = `Contacts_${formattedDate}.xlsx`; // ✅ dynamic name

      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          fileName,
        );
      });
    });

    e.cancel = true; // ❗ VERY IMPORTANT (stops default export)
  };

  return (
    <>
      {loading && <LoadPanel visible={true} />}

      <div className="wholePage">
        <div className="headerComponent">
          <div className="headerContainer1">
            <div className="title">
              <span class="icon-size">
                <i class="ri-contacts-book-2-line"></i>
              </span>
              <p>All Contacts</p>
            </div>
            {/* <div className="totalTicketTitle">
              <SubText text={`In total, you have ${visibleCount} contacts`} />
            </div> */}
          </div>
          <div className="headerContainer2">
            <Button
              text="Export"
              icon="export"
              stylingMode="outlined"
              type="default"
              width={120}
              onClick={handleExport}
            />
            <Button
              className="addBtn"
              text="Add Contact"
              icon="add"
              stylingMode="outlined"
              type="default"
              width={140}
              onClick={handleCreateNew}
            />
          </div>
        </div>
        {/* <CustomPageHeader
          // titleName={"Contacts"}
          handleCreateNew={handleCreateNew}
          btnText={"Add Contact"}
        /> */}
        <div
          className="mainDatagrid middlePart"
          style={{
            border: "1px solid #d9dde1",
            margin: "6px 6px 0px",
            borderRadius: "8px",
          }}
        >
          <DataGrid
            height="calc(100vh - 200px)"
            width={"100%"}
            className={"contactGrid"}
            dataSource={contactDataSource}
            ref={dataGridRef}
            allowColumnReordering={true}
            rowAlternationEnabled={false}
            columnAutoWidth={true}
            columnHidingEnabled={true}
            showColumnHeaders={true}
            keyExpr="contactemail"
            onContentReady={(e) => {
              const visibleRows = e.component.getVisibleRows();
              setVisibleCount(visibleRows.length);
            }}
            onExporting={onExporting}
          >
            <Export enabled={true} />
            <Toolbar>
              <Item location="before">
                <div style={{ fontWeight: "600", fontSize: "18px" }}>
                  <SubText text={`Total ${visibleCount} contacts`} />
                </div>
              </Item>
              <Item location="after">
                <SelectBox
                  width={150}
                  height={44}
                  id="filterStatus"
                  valueExpr="value"
                  displayExpr="text"
                  stylingMode="outlined"
                  className="selectboxStatus"
                  placeholder="All Status"
                  items={[
                    { value: "All", text: "All Status" },
                    { value: "Active", text: "Active" },
                    { value: "Inactive", text: "Inactive" },
                  ]}
                  value={statusFilter}
                  onValueChanged={(e) => handleStatusFilterChange(e.value)}
                />
              </Item>
              <Item name="searchPanel" />
            </Toolbar>

            <SearchPanel visible={true} width={200} placeholder="Search" />
            <Selection mode="multiple" showCheckBoxesMode="always" />

            <Pager
              visible={true}
            // allowedPageSizes={[10, 20, 30]}
            // showPageSizeSelector={true}
            />
            <Paging defaultPageSize={10} />
            <Scrolling mode="virtual" />

            {/* Correct Fields From API */}
            <Column dataField="contactname" caption="CONTACT NAME" />
            <Column
              width={60}
              alignment="center"
              allowSorting={false}
              allowSearch={false}
              cellRender={actionTemplate}
            />
            <Column
              dataField="status"
              caption="CONTACT STATUS"
              cellRender={(cellData) => {
                const status = cellData.value;

                return (
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "4px 10px",
                      borderRadius: "14px",
                      fontWeight: 500,
                      fontSize: "14px",

                      color: getStatusColor(status),
                    }}
                  >
                    <span
                      style={{
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        backgroundColor: getStatusColor(status),
                      }}
                    ></span>
                    {status}
                  </div>
                );
              }}
            />
            <Column dataField="contactcode" caption="CONTACT CODE" />
            <Column dataField="contactemail" caption="EMAIL" />
            <Column
              dataField="contactnumber"
              caption="PHONE NUMBER"
              cellRender={(data) => {
                return maskPhoneNumber(data?.data?.contactnumber);
              }}
            />
            <Column
              dataField="departmentname"
              caption="DEPARTMENT"
              cellRender={(cellData) => {
                return cellData.value ? cellData.value : "--";
              }}
            />
          </DataGrid>
        </div>
      </div>
    </>
  );
}
